import { SEVERITY_SCALE } from "../../../whiteLabel/whiteLabelTheme";
import FillLines from "../components/FillLines";
import SectionHeading from "../components/SectionHeading";
import WhiteLabelCard from "../components/WhiteLabelCard";
import WhiteLabelPage from "../WhiteLabelPage/WhiteLabelPage";
import { WhiteLabelPageProps } from "../types";

import classes from "./NutritionGuidePage.module.css";

/** Blank write-in rows per column, from the client's mock-up. */
const EAT_LIMIT_AVOID_ROWS = 8;
/** Chosen so the rule rhythm in the lower sections matches the columns above. */
const TARGET_ROW_H = 34;

const NutritionGuidePage = ({
  theme,
  lang,
  t,
  pageNumber,
}: WhiteLabelPageProps): JSX.Element => {
  // Eat / Limit / Avoid reuse the fixed severity hues so the guidance reads with
  // the same colour language as the score bands on page 1.
  const columns = [
    { label: t.nutritionGuide.columns.eat, color: theme.colors.accentA },
    { label: t.nutritionGuide.columns.limit, color: SEVERITY_SCALE[1].color },
    { label: t.nutritionGuide.columns.avoid, color: SEVERITY_SCALE[0].color },
  ];

  // 1030 content height - 2 gaps - title block, split 50/30/20.
  const bodyH = 1030 - 28 - 92;
  const otherRows = Math.max(
    3,
    Math.floor((bodyH * 0.3 - 50) / TARGET_ROW_H)
  );
  const supplementRows = Math.max(
    2,
    Math.floor((bodyH * 0.2 - 50) / TARGET_ROW_H)
  );

  return (
    <WhiteLabelPage
      theme={theme}
      lang={lang}
      brandLabelTemplate={t.footer.brandLabel}
      sectionName={t.sections.nutritionGuide}
      pageNumber={pageNumber}
      dataAgentTarget="wl-nutrition-guide"
    >
      <div className={classes.pageTitle}>{t.nutritionGuide.heading}</div>
      <div className={classes.pageSubtitle}>{t.nutritionGuide.subtitle}</div>

      <div className={classes.body}>
        <div className={classes.section50}>
          <div className={classes.sectionLabel}>
            {t.nutritionGuide.eatLimitAvoidHeading}
          </div>
          <WhiteLabelCard fill>
            <div className={classes.columns}>
              {columns.map((column) => (
                <div className={classes.column} key={column.label}>
                  <div className={classes.columnHeading}>
                    <SectionHeading
                      label={column.label}
                      color={column.color}
                    />
                  </div>
                  <FillLines rows={EAT_LIMIT_AVOID_ROWS} />
                </div>
              ))}
            </div>
          </WhiteLabelCard>
        </div>

        {/* The two write-in cards drop their top padding so the first rule sits
            flush with the card's top edge. The Eat/Limit/Avoid card above keeps
            its padding, since its column headings need the breathing room. */}
        <div className={classes.section30}>
          <div className={classes.sectionLabel}>
            {t.nutritionGuide.otherRecommendations}
          </div>
          <WhiteLabelCard fill padding="0 16px 14px">
            <FillLines rows={otherRows} />
          </WhiteLabelCard>
        </div>

        <div className={classes.section20}>
          <div className={classes.sectionLabel}>
            {t.nutritionGuide.supplements}
          </div>
          <WhiteLabelCard fill padding="0 16px 14px">
            <FillLines rows={supplementRows} />
          </WhiteLabelCard>
        </div>
      </div>
    </WhiteLabelPage>
  );
};

export default NutritionGuidePage;
